import React from 'react';
import { AIRun, isRunFinished } from './aiRun';

/**
 * How long the run has been going, in seconds.
 *
 * `storageKey` is for flows that stamp their start into `localStorage` when
 * they kick the run off — matter creation does, so a remount mid-run resumes
 * the clock instead of restarting it at zero. Both spellings that get written
 * are accepted: a millisecond stamp and an ISO string.
 *
 * The clock stops the moment the run reaches a terminal state, so the panel
 * keeps showing how long the work actually took rather than counting on past
 * the end of it.
 */
export function useAIRunElapsed(run: AIRun, storageKey?: string): number {
  const [seconds, setSeconds] = React.useState(0);
  const startedAt = run.startedAt;
  const finished = isRunFinished(run);

  React.useEffect(() => {
    const startMs = (): number | null => {
      if (storageKey) {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const ms = /^\d+$/.test(stored.trim())
            ? Number(stored.trim())
            : new Date(stored).getTime();
          if (Number.isFinite(ms) && ms > 0) return ms;
        }
      }
      return startedAt;
    };

    const tick = () => {
      const start = startMs();
      setSeconds(start ? Math.max(0, Math.floor((Date.now() - start) / 1000)) : 0);
    };

    tick();
    if (finished) return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [storageKey, startedAt, finished]);

  return seconds;
}

export default useAIRunElapsed;
